// src/pages/app_media_monitor.js
import React, { useEffect, useMemo, useState } from "react";
import PermissionGuard from "../components/PermissionGuard";
import { db, storage } from "../firebase";
import {
  collection,
  onSnapshot,
  query as q,
  orderBy,
  limit as qLimit,
} from "firebase/firestore";
import { getDownloadURL, ref as storageRef } from "firebase/storage";
import {
  FiRefreshCw,
  FiSearch,
  FiDownload,
  FiExternalLink,
  FiAlertTriangle,
} from "react-icons/fi";

/** Brand */
const COLORS = {
  primary: "#f20c53",
  secondary: "#fb6c33",
  text: "#0f172a",
  subtext: "#475569",
  border: "#e2e8f0",
  card: "#ffffff",
  page: "#f5f6f8",
  ok: "#16a34a",
  warn: "#b91c1c",
  info: "#0ea5e9",
};

const DEFAULT_LIMIT = 500;

export default function AppMediaMonitor() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [status, setStatus] = useState("all"); // all | success | skipped | failed
  const [qText, setQText] = useState("");
  const [toast, setToast] = useState("");

  useEffect(() => {
    const ref = collection(db, "media_optimizations");
    const unsub = onSnapshot(
      q(ref, orderBy("ts", "desc"), qLimit(Math.max(50, Math.min(5000, limit)))),
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setRows(list);
        setLoading(false);
      }
    );
    return () => unsub();
  }, [limit]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 1500);
    return () => clearTimeout(t);
  }, [toast]);

  const filtered = useMemo(() => {
    let r = rows;
    if (status !== "all") r = r.filter((x) => (x.status || "").toLowerCase() === status);
    const qq = qText.trim().toLowerCase();
    if (qq) {
      r = r.filter((x) =>
        [x.path, x.type, x.bucket, String(x.originalBytes), String(x.optimizedBytes), x.error]
          .join(" ")
          .toLowerCase()
          .includes(qq)
      );
    }
    return r;
  }, [rows, status, qText]);

  const stats = useMemo(() => {
    const ok = filtered.filter((x) => (x.status || "").toLowerCase() === "success");
    const fail = filtered.filter((x) => (x.status || "").toLowerCase() === "failed");
    const skipped = filtered.filter((x) => (x.status || "").toLowerCase() === "skipped");

    const orig = ok.reduce((s, x) => s + num(x.originalBytes), 0);
    const opt = ok.reduce((s, x) => s + num(x.optimizedBytes), 0);
    const saved = Math.max(0, orig - opt);
    const pct = orig ? (saved / orig) * 100 : 0;

    const imgCount = ok.filter((x) => (x.type || "").startsWith("image")).length;
    const vidCount = ok.filter((x) => (x.type || "").startsWith("video")).length;

    return {
      total: filtered.length,
      okCount: ok.length,
      failCount: fail.length,
      skippedCount: skipped.length,
      origBytes: orig,
      optBytes: opt,
      savedBytes: saved,
      savedPct: pct,
      imgCount,
      vidCount,
    };
  }, [filtered]);

  async function openFile(path) {
    try {
      const url = await getDownloadURL(storageRef(storage, path));
      window.open(url, "_blank", "noopener,noreferrer");
    } catch {
      setToast("Cannot get file URL");
    }
  }

  function exportCSV() {
    const header = [
      "id",
      "path",
      "type",
      "status",
      "originalBytes",
      "optimizedBytes",
      "savedBytes",
      "savedPct",
      "bucket",
      "ts",
      "error",
    ];
    const lines = [header.join(",")];
    filtered.forEach((x) => {
      const saved = Math.max(0, num(x.originalBytes) - num(x.optimizedBytes));
      const pct = num(x.originalBytes) > 0 ? ((saved / num(x.originalBytes)) * 100).toFixed(2) : "0";
      lines.push(
        [
          x.id,
          safeCSV(x.path),
          x.type || "",
          x.status || "",
          num(x.originalBytes),
          num(x.optimizedBytes),
          saved,
          pct,
          x.bucket || "",
          x.ts ? new Date(x.ts).toISOString() : "",
          safeCSV(x.error || ""),
        ].join(",")
      );
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `media_optimizations_${Date.now()}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  return (
    <PermissionGuard requiredPermission="view_media_monitor">
      <div
        style={{
          padding: 16,
          background: COLORS.page,
          minHeight: "100vh",
          fontFamily: "Inter, system-ui, sans-serif",
        }}
      >
        {/* Header */}
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: 0, color: COLORS.text, fontWeight: 900 }}>Media Optimization Monitor</h2>
            <p style={{ margin: "4px 0 0", color: COLORS.subtext }}>
              Live view of Storage optimize jobs from <code>media_optimizations</code>.
            </p>
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))",
              gap: 8,
              minWidth: 320,
            }}
          >
            <Stat label="Processed" value={stats.total} tone="info" />
            <Stat
              label="Saved"
              value={`${bytes(stats.savedBytes)} • ${stats.savedPct.toFixed(1)}%`}
              tone="ok"
            />
            <Stat label="Errors" value={stats.failCount} tone={stats.failCount ? "warn" : "muted"} />
          </div>
        </div>

        {/* Controls */}
        <div style={{ marginTop: 12, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
          <div style={searchBox}>
            <FiSearch />
            <input
              value={qText}
              onChange={(e) => setQText(e.target.value)}
              placeholder="Search by path/type/status…"
              style={searchInput}
            />
          </div>

          <select value={status} onChange={(e) => setStatus(e.target.value)} style={sel}>
            <option value="all">All statuses</option>
            <option value="success">Success</option>
            <option value="skipped">Skipped</option>
            <option value="failed">Failed</option>
          </select>

          <select
            value={limit}
            onChange={(e) => setLimit(Number(e.target.value) || DEFAULT_LIMIT)}
            style={sel}
          >
            <option value="200">Last 200</option>
            <option value="500">Last 500</option>
            <option value="1000">Last 1000</option>
            <option value="2000">Last 2000</option>
            <option value="5000">Last 5000</option>
          </select>

          <button onClick={() => setLimit((x) => x)} style={btn}>
            <FiRefreshCw /> Refresh
          </button>
          <button onClick={exportCSV} style={btn}>
            <FiDownload /> Export CSV
          </button>
        </div>

        {/* Summary chips */}
        <div style={{ marginTop: 10, display: "flex", gap: 8, flexWrap: "wrap" }}>
          <Chip label="Images" value={stats.imgCount} />
          <Chip label="Videos" value={stats.vidCount} />
          <Chip label="Success" value={stats.okCount} />
          <Chip label="Skipped" value={stats.skippedCount} />
          <Chip label="Failed" value={stats.failCount} />
          <Chip label="Original bytes" value={bytes(stats.origBytes)} />
          <Chip label="Optimized bytes" value={bytes(stats.optBytes)} />
        </div>

        {/* Table */}
        <div
          style={{
            marginTop: 12,
            background: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 12,
            overflow: "hidden",
          }}
        >
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
              <thead>
                <tr>
                  <Th>Path</Th>
                  <Th>Type</Th>
                  <Th>Status</Th>
                  <Th>Original</Th>
                  <Th>Optimized</Th>
                  <Th>Saved</Th>
                  <Th>When</Th>
                  <Th>Actions</Th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <Td colSpan={8}>Loading…</Td>
                  </tr>
                ) : filtered.length === 0 ? (
                  <tr>
                    <Td colSpan={8} style={{ color: COLORS.subtext }}>
                      No rows
                    </Td>
                  </tr>
                ) : (
                  filtered.map((r, idx) => {
                    const saved = Math.max(0, num(r.originalBytes) - num(r.optimizedBytes));
                    const pct = num(r.originalBytes) ? (saved / num(r.originalBytes)) * 100 : 0;
                    const isFail = (r.status || "").toLowerCase() === "failed";
                    return (
                      <tr
                        key={r.id}
                        style={{
                          borderTop: `1px solid ${COLORS.border}`,
                          background: idx % 2 ? "#fafafa" : "#fff",
                        }}
                      >
                        <Td mono>
                          <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                            <span title={r.path}>{truncate(r.path, 64)}</span>
                            {isFail && (
                              <span
                                title={r.error}
                                style={{
                                  color: COLORS.warn,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                }}
                              >
                                <FiAlertTriangle /> error
                              </span>
                            )}
                          </div>
                        </Td>
                        <Td>{r.type || "—"}</Td>
                        <Td>
                          <StatusPill v={r.status} />
                        </Td>
                        <Td>{bytes(r.originalBytes)}</Td>
                        <Td>{bytes(r.optimizedBytes)}</Td>
                        <Td>
                          {bytes(saved)} {num(r.originalBytes) ? `• ${pct.toFixed(1)}%` : ""}
                        </Td>
                        <Td>{r.ts ? timeFmt(r.ts) : "—"}</Td>
                        <Td>
                          <div style={{ display: "flex", gap: 8 }}>
                            <button style={iconBtn} title="Open" onClick={() => openFile(r.path)}>
                              <FiExternalLink />
                            </button>
                          </div>
                        </Td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>

        {toast && <div style={toastBox}>{toast}</div>}
      </div>
    </PermissionGuard>
  );
}

/* UI bits */
function Stat({ label, value, tone }) {
  const palette =
    tone === "ok"
      ? { bg: "#ecfdf5", bd: "#a7f3d0", fg: "#065f46" }
      : tone === "warn"
      ? { bg: "#fff1f2", bd: "#fecdd3", fg: "#9f1239" }
      : tone === "info"
      ? { bg: "#eff6ff", bd: "#bfdbfe", fg: "#1e3a8a" }
      : { bg: "#fff", bd: COLORS.border, fg: COLORS.text };
  return (
    <div
      style={{
        background: palette.bg,
        border: `1px solid ${palette.bd}`,
        borderRadius: 10,
        padding: "10px 12px",
        minWidth: 150,
      }}
    >
      <div style={{ fontSize: 12, color: COLORS.subtext }}>{label}</div>
      <div style={{ fontWeight: 900, color: palette.fg, fontSize: 18 }}>{value}</div>
    </div>
  );
}
function Chip({ label, value }) {
  return (
    <div
      style={{
        display: "inline-flex",
        gap: 8,
        alignItems: "center",
        padding: "6px 10px",
        borderRadius: 999,
        border: `1px solid ${COLORS.border}`,
        background: "#fff",
      }}
    >
      <strong style={{ color: COLORS.text }}>{label}:</strong>
      <span style={{ color: COLORS.subtext }}>{value}</span>
    </div>
  );
}
function Th({ children }) {
  return (
    <th
      style={{
        textAlign: "left",
        padding: 10,
        fontWeight: 800,
        color: COLORS.text,
        background: "#f8fafc",
        borderBottom: `1px solid ${COLORS.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {children}
    </th>
  );
}
function Td({ children, mono = false, colSpan, style }) {
  return (
    <td
      colSpan={colSpan}
      style={{
        padding: 10,
        verticalAlign: "top",
        color: COLORS.text,
        fontFamily: mono ? "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace" : undefined,
        ...style,
      }}
    >
      {children}
    </td>
  );
}
function StatusPill({ v }) {
  const s = String(v || "").toLowerCase();
  const map = {
    success: { bg: "#dcfce7", fg: "#166534" },
    skipped: { bg: "#f1f5f9", fg: "#334155" },
    failed: { bg: "#fee2e2", fg: "#991b1b" },
  };
  const m = map[s] || { bg: "#e5e7eb", fg: "#111827" };
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 8px",
        borderRadius: 999,
        background: m.bg,
        color: m.fg,
        fontWeight: 800,
        fontSize: 12,
      }}
    >
      {v || "—"}
    </span>
  );
}

/* Styles */
const searchBox = {
  display: "flex",
  alignItems: "center",
  gap: 8,
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  background: "#fff",
  minWidth: 280,
};
const searchInput = { border: "none", outline: "none", flex: 1, fontSize: 14 };
const sel = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  borderRadius: 10,
  padding: "10px 12px",
};
const btn = {
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "10px 12px",
  borderRadius: 10,
  border: "none",
  color: "#fff",
  background: `linear-gradient(90deg, ${COLORS.primary}, ${COLORS.secondary})`,
  cursor: "pointer",
  fontWeight: 800,
};
const iconBtn = {
  border: `1px solid ${COLORS.border}`,
  background: "#fff",
  color: COLORS.text,
  width: 34,
  height: 34,
  borderRadius: 8,
  display: "grid",
  placeItems: "center",
  cursor: "pointer",
};
const toastBox = {
  position: "fixed",
  bottom: 16,
  right: 16,
  background: "#fff",
  border: `1px solid ${COLORS.border}`,
  borderRadius: 10,
  padding: "10px 12px",
  boxShadow: "0 8px 24px rgba(0,0,0,.12)", // ← fixed: quotes match
  color: COLORS.text,
  fontWeight: 800,
  zIndex: 80,
};

/* Utils */
function num(v) {
  const n = Number(v || 0);
  return isNaN(n) ? 0 : n;
}
function bytes(v) {
  const n = num(v);
  if (n < 1024) return `${n} B`;
  if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
  return `${(n / 1024 ** 3).toFixed(1)} GB`;
}
function timeFmt(v) {
  try {
    const d = typeof v === "number" ? new Date(v) : new Date(String(v));
    return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(d);
  } catch {
    return "—";
  }
}
function truncate(s = "", n = 64) {
  return s.length > n ? s.slice(0, n - 3) + "..." : s;
}
function safeCSV(s = "") {
  const v = String(s).replace(/"/g, '""');
  return `"${v}"`;
}
